// src/components/Header.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react'; // Removed useMemo
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';
import { hasRole, isAuthenticated } from '../utils/auth';
import { useConfig } from '../context/ConfigContext';
import CustomerProfileModal from './CustomerProfileModal';
import { fetchCustomer } from '../utils/api';
import { Customer } from '../utils/types';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import LanguageSwitcher from './LanguageSwitcher'; // Import LanguageSwitcher
import Zoom from './Zoom'; // Import the new Zoom component

const DEFAULT_ZOOM_LEVEL = 100; // 100%
const ZOOM_STEP = 10; // 10% increment/decrement
const MIN_ZOOM = 50; // Minimum zoom level
const MAX_ZOOM = 200; // Maximum zoom level


const Header = () => {
    const navRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation(); // Removed i18n

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { settings, loadingSettings, errorSettings } = useConfig();
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer, setSelectedSessionCustomer } = useCustomerContext();

    // --- State for Profile Modal ---
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [profileCustomerData, setProfileCustomerData] = useState<Customer | null>(null);
    const [loadingProfile, setLoadingProfile] = useState(false);
    // --- State to track which button triggered loading ---
    const [loadingTrigger, setLoadingTrigger] = useState<'actingAs' | 'email' | null>(null);

    // --- Refs for the buttons ---
    const actingAsButtonRef = useRef<HTMLButtonElement>(null);
    const emailButtonRef = useRef<HTMLButtonElement>(null);


    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    useEffect(() => {
        if (!isMenuOpen) return;

        function handleClickOutside(event: MouseEvent) {
            if (
                navRef.current &&
                !navRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);

    // Initialize zoomLevel from localStorage or default
    const [zoomLevel, setZoomLevel] = useState<number>(() => {
        const storedZoom = localStorage.getItem('appZoomLevel');
        return storedZoom ? parseInt(storedZoom, 10) : DEFAULT_ZOOM_LEVEL;
    });

    useEffect(() => {
        document.body.style.fontSize = `${zoomLevel}%`;
        localStorage.setItem('appZoomLevel', zoomLevel.toString());
    }, [zoomLevel]);

    const handleZoomIn = useCallback(() => {
        setZoomLevel(prevZoom => Math.min(prevZoom + ZOOM_STEP, MAX_ZOOM));
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoomLevel(prevZoom => Math.max(prevZoom - ZOOM_STEP, MIN_ZOOM));
    }, []);

    const handleResetZoom = useCallback(() => {
        setZoomLevel(DEFAULT_ZOOM_LEVEL);
    }, []);

    // Constants for zoom levels ends here


    // Determine if admin/staff is acting as another customer
    const isAdminOrStaff = hasRole(['super_admin', 'staff', 'technician']);
    let displayCustomerName = loggedInUser?.name || 'Guest';
    let displayCustomerId = loggedInUser?.id; // Logged-in user's ID by default
    let isActingAsCustomer = false;

    if (isAdminOrStaff && selectedSessionCustomer) {
        displayCustomerName = selectedSessionCustomer.name || 'Selected Customer';
        displayCustomerId = selectedSessionCustomer.id; // Use selected customer's ID
        isActingAsCustomer = true;
    }

    const handleClearSelection = () => {
        setSelectedSessionCustomer(null);
    };

    // --- Updated Profile Modal Handler ---
    // Accept a trigger source identifier
    const handleOpenProfileModal = async (triggerSource: 'actingAs' | 'email') => {
        let customerIdToFetch: number | null = null;
        let customerNameToFetch: string | null = null;

        if (loadingProfile) return;

        // Set the trigger source
        setLoadingTrigger(triggerSource);

        if (isActingAsCustomer && selectedSessionCustomer) {
            customerIdToFetch = selectedSessionCustomer.id;
            customerNameToFetch = selectedSessionCustomer.name;
            console.log("Modal requested for 'Acting As' customer:", customerIdToFetch);
        } else if (!isActingAsCustomer && loggedInUser) {
            console.log("Modal requested for logged-in user's profile.");
            try {
                const storedCustomerId = localStorage.getItem('customer');
                // console.log("storedCustomerId if !isActingAsCustomer && loggedInUser:", storedCustomerId);

                if (storedCustomerId) {
                    const parsedId = JSON.parse(storedCustomerId).id;

                    console.log("Parsed customer ID from localStorage:", parsedId);

                    if (typeof parsedId === 'number') {
                        customerIdToFetch = parsedId;
                        customerNameToFetch = loggedInUser.name;
                    } else {
                        console.error("Invalid customer ID format in localStorage:", storedCustomerId);
                    }
                }
            } catch (e) {
                console.error("Failed to parse customer ID from localStorage:", e);
            }
        } else {
            toast.error("User or customer information missing.");
            setLoadingTrigger(null); // Reset trigger on early exit
            return;
        }

        if (!customerIdToFetch) {
            toast.error("Associated customer ID not found. Cannot load profile.");
            console.error("Customer ID missing or invalid:", customerIdToFetch);
            setLoadingTrigger(null); // Reset trigger on early exit
            return;
        }

        setLoadingProfile(true);
        setIsProfileModalOpen(true);
        setProfileCustomerData(null);

        try {
            // console.log("Fetching customer data for customer ID:", customerIdToFetch);
            const customerData = await fetchCustomer(customerIdToFetch);
            setProfileCustomerData(customerData);
        } catch (err: any) {
            console.error("Failed to load customer profile:", err);
            const nameForError = customerNameToFetch || `ID ${customerIdToFetch}`;
            if (err.response?.status === 404) {
                toast.error(`Customer profile not found for ${nameForError}.`);
            } else {
                toast.error(err.response?.data?.message || err.message || `Failed to load profile for ${nameForError}.`);
            }
            setIsProfileModalOpen(false);
        } finally {
            setLoadingProfile(false);
            setLoadingTrigger(null); // Reset trigger when loading finishes
        }
    };
    // --- End Updated Profile Modal Handler ---

    const handleCloseProfileModal = () => {
        setIsProfileModalOpen(false);
        setProfileCustomerData(null);
    };

    // --- Loading/Error states for settings ---
    if (loadingSettings) {
        return <header className="sticky top-0 z-30 bg-blue-800 text-white px-4 py-1 text-center">Loading settings...</header>;
    }

    if (errorSettings) {
        return <header className="sticky top-0 z-30 bg-red-600 text-white px-4 py-1 text-center">Error loading settings: {errorSettings}</header>;
    }

    return (
        <>
            <header className="w-full sticky top-0 z-30 flex items-center justify-between bg-blue-800 text-white pl-4 pr-1 py-1">
                {/* Company Name */}
                <h1 className="text-lg md:text-2xl">
                    <Link to="/">
                        {/* Mobile: show initials if shortname > 5 chars, else show full name; Desktop: always show full name */}
                        <span className="md:hidden" title={settings?.shortname || undefined}>
                            {settings?.shortname && settings.shortname.length > 10
                                ? settings.shortname.split(/\s+/).map((word: string) => word[0]).join('').toUpperCase()
                                : (settings?.shortname || t('header.companyName'))}
                        </span>
                        <span className="hidden md:inline">
                            {settings?.shortname && settings.shortname.length > 26
                                ? settings.shortname.split(/\s+/).map((word: string) => word[0]).join('').toUpperCase()
                                : (settings?.shortname || t('header.companyName'))}
                        </span>
                    </Link>
                </h1>

                <div className='flex flex-row justify-between items-center md:hidden'>

                    <Zoom
                        zoomLevel={zoomLevel}
                        minZoom={MIN_ZOOM}
                        maxZoom={MAX_ZOOM}
                        onZoomIn={handleZoomIn}
                        onZoomOut={handleZoomOut}
                        onResetZoom={handleResetZoom}
                    />
                    <span className="ml-2 md:hidden"><LanguageSwitcher /></span>

                    {/* Hamburger Menu Button (Mobile) */}

                    <button
                        className="mx-4 text-white focus:outline-none md:hidden"
                        onClick={toggleMenu}
                        aria-label="Toggle menu"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
                        </svg>
                    </button>



                </div>
                {/* Navigation Links */}
                <nav
                    ref={navRef}
                    className={`absolute md:static top-full left-0 right-0 text-xl md:text-sm bg-blue-600  rounded-b-xl shadow-xl p-5 w-full md:flex md:items-center md:w-auto md:p-0

                        

                    md:bg-transparent ${isMenuOpen ? 'block' : 'hidden'}`}>

                    {isAuthenticated() ? (
                        <>

                            <div className=' border-b-2 md:border-none py-4 md:py-0'>
                                {/* Standard Links */}
                                {isAdminOrStaff && (
                                    <Link to="/customer-management" className="font-bold text-lg block md:inline-block mr-4 py-1 md:py-0 text-blue-50 md:text-white hover:text-gray-100" onClick={() => setIsMenuOpen(false)}>{t('header.customerManagement')}</Link>
                                )}

                                <Link to="/logout" className="font-bold text-lg block md:inline-block mr-4 py-1 md:py-0 text-blue-50 md:text-white  hover:text-yellow-100" onClick={() => setIsMenuOpen(false)}>{t('header.logout')}</Link>
                            </div>

                            {/* --- Updated Acting As Info --- */}
                            {isActingAsCustomer && (
                                <div className="text-sm bg-yellow-300 text-black p-2 rounded-xl mt-4 md:mt-0  mb-2 md:mb-0 md:mr-4 md:order-first flex items-center gap-1 flex-wrap">
                                    <span>Acting as: {displayCustomerName} (</span>
                                    {/* Make ID clickable - Using t function for "Loading..." and "Profile ID" */}
                                    <button
                                        ref={actingAsButtonRef} // Add ref
                                        onClick={() => handleOpenProfileModal('actingAs')} // Pass trigger source
                                        disabled={loadingProfile}
                                        className="font-semibold underline hover:text-blue-700 disabled:opacity-50 disabled:cursor-wait disabled:no-underline"
                                        title={`View/Edit profile for ${displayCustomerName}`}
                                    >
                                        {/* Check trigger source for loading text */}
                                        {loadingProfile && loadingTrigger === 'actingAs' ? t('header.loading') : t('header.profileId', { id: displayCustomerId })}
                                    </button>
                                    <span>)</span>
                                    <button onClick={handleClearSelection} className="ml-auto text-red-700 underline text-xs font-bold pl-2">({t('header.clear')})</button>
                                </div>
                            )}
                            {/* --- End Updated Acting As Info --- */}


                            {/* User Info/Role Badges */}
                            <div className="relative group mt-4 md:mt-0 md:ml-auto flex flex-col md:flex-row md:items-center gap-2 border-b-2 md:border-none pb-4 md:pb-0">
                                {/* Badge (always visible) */}
                                <span
                                    className="hidden md:inline-block bg-green-500 text-white px-2 py-1 rounded text-xs font-bold cursor-pointer"
                                    onClick={() => handleOpenProfileModal('email')} // Open profile modal on click
                                    title={t('header.viewEditProfile')}
                                >
                                    {loggedInUser?.name
                                        ? loggedInUser.name
                                            .split(' ')
                                            .filter(Boolean)
                                            .map(word => word[0])
                                            .join('')
                                            .toUpperCase()
                                        : "U"}
                                </span>

                                {/* Full info (hidden by default, show on hover) */}
                                <div
                                    className="
     sm:block
    md:absolute md:left-0 md:top-full md:mt-0 md:z-50 md:bg-neutral-300/20 md:text-black md:rounded md:shadow-xl md:p-4 md:min-w-[200px]
    md:opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto md:transition-all md:duration-200
    bg-neutral-300/20 hover:bg-neutral-300/30 backdrop-blur-[1px] border border-neutral-400/20

  "
                                >
                                    <button
                                        ref={emailButtonRef}
                                        onClick={() => handleOpenProfileModal('email')}
                                        disabled={loadingProfile}
                                        className={`text-xs md:text-sm text-blue-900 
bg-neutral-100/90 hover:bg-neutral-100/60 backdrop-blur-[1px] border border-neutral-400/90 w-full 
                                            mr-2 px-3 py-2 rounded-lg shadow-md hover:bg-green-200 border border-white focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-wait transition-colors duration-150`}
                                        title={t('header.viewEditProfile')}
                                    >
                                        {t('header.profile')}
                                        {loadingProfile && loadingTrigger === 'email' ? t('header.loading') : loggedInUser?.email}
                                        <p className='text-xs md:text-xs bg-gray-500 mt-2 mr-2 px-3 py-2 rounded-lg border border-white'>
                                            {hasRole(['panel_user']) && <span className='text-blue-100'>Panel User</span>}
                                            {hasRole(['staff']) && <span className='text-pink-100'>Staff</span>}
                                            {hasRole(['dealer']) && <span className='text-purple-100'>Dealer</span>}
                                            {hasRole(['fl']) && <span className='text-green-100'>FL</span>}
                                            {hasRole(['super_admin']) && <span className='text-red-100'>Super Admin</span>}
                                        </p>
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Login/Register Links */}
                            <Link to="/login" className="block md:inline-block mr-4 py-1 md:py-0 hover:text-gray-100" onClick={() => setIsMenuOpen(false)}>{t('header.login')}</Link>

                            <Link to="/register" className="block md:inline-block mr-4 py-1 md:py-0 hover:text-gray-100" onClick={() => setIsMenuOpen(false)}>{t('header.register')}</Link>

                        </>
                    )}

                    <div className="flex flex-row flex-grow justify-end md:justify-end mt-4 md:mt-0 space-x-4">
                        {/* Language Switcher */}
                        <LanguageSwitcher />

                        {/* Zoom Controls */}
                        <Zoom
                            zoomLevel={zoomLevel}
                            minZoom={MIN_ZOOM}
                            maxZoom={MAX_ZOOM}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                            onResetZoom={handleResetZoom}
                        />
                    </div>

                </nav>

            </header>

            {/* --- Render Profile Modal (No changes needed here) --- */}
            {isProfileModalOpen && (
                <>
                    {loadingProfile && (
                        // Simple loading state modal shell
                        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                            <div className="bg-white p-6 rounded-lg shadow-xl">

                                <p className="text-gray-600">Loading profile...</p>
                            </div>
                        </div>
                    )}
                    {!loadingProfile && profileCustomerData && (
                        // Render CustomerProfileModal directly and pass onClose
                        <CustomerProfileModal
                            initialCustomerData={profileCustomerData}
                            onClose={handleCloseProfileModal} // Pass the onClose prop
                        />
                    )}
                    {!loadingProfile && !profileCustomerData && ( // Error case after loading
                        // Simple error state modal shell
                        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
                                <p>Could not load customer profile data.</p>
                                <button
                                    onClick={handleCloseProfileModal}
                                    className="mt-4 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                                >
                                    Close
                                </button>
                            </div>

                        </div>
                    )}
                </>
            )}
            {/* --- End Render Profile Modal --- */}
        </>
    );
};

export default Header;
